// Balance Sheet hook — mirrors the logic used in the workbook's
// "Balance Sheet-ETB" (which is Balance Sheet-USD × FX) but sources
// data live from this app.
//
// See .lovable/plan.md → "Mapping to our app's data".

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  eachMonthOfInterval,
  eachQuarterOfInterval,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  getQuarter,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  differenceInCalendarDays,
} from "date-fns";

import { supabase } from "@/integrations/supabase/client";
import { paginateSupabase } from "@/lib/supabase-paginate";
import { convertAsOfForAccount, type RateRow } from "@/lib/fx";

export type ViewPeriod = "monthly" | "quarterly" | "yearly";
export type DisplayCurrency = "ETB" | "USD";

export type BSRow = {
  key: string;
  label: string;
  isHeader?: boolean;
  isSubtotal?: boolean;
  isTotal?: boolean;
  isCheck?: boolean;
  indent?: number;
  values: Map<string, number>;
};

export type Period = { key: string; label: string; end: string; start: string };

// Assets category (Admin Cost → Asset) — cumulative "fixed assets (accumulated cost)"
const ASSET_CATEGORY_ID = "de427b28-764e-4334-b4c4-79273cfe31c1";
const DEPRECIATION_MONTHS = 60; // straight-line default (5 years)

// Anchor for pulling all history when reconstructing cash and cumulative Assets.
const HISTORY_START = "2020-01-01";

// ---------- period helpers ----------

function buildPeriods(dateFrom: string, dateTo: string, viewPeriod: ViewPeriod): Period[] {
  const from = parseISO(dateFrom);
  const to = parseISO(dateTo);

  if (viewPeriod === "monthly") {
    return eachMonthOfInterval({ start: from, end: to }).map((d) => {
      const end = endOfMonth(d);
      return {
        key: format(d, "yyyy-MM"),
        label: format(d, "MMM yyyy"),
        start: format(startOfMonth(d), "yyyy-MM-dd"),
        end: format(end > to ? to : end, "yyyy-MM-dd"),
      };
    });
  }
  if (viewPeriod === "quarterly") {
    return eachQuarterOfInterval({ start: from, end: to }).map((d) => {
      const end = endOfQuarter(d);
      return {
        key: `${format(d, "yyyy")}-Q${getQuarter(d)}`,
        label: `${format(d, "yyyy")} Q${getQuarter(d)}`,
        start: format(startOfQuarter(d), "yyyy-MM-dd"),
        end: format(end > to ? to : end, "yyyy-MM-dd"),
      };
    });
  }
  // yearly
  const years: Period[] = [];
  let y = from.getUTCFullYear();
  const endY = to.getUTCFullYear();
  while (y <= endY) {
    const s = new Date(Date.UTC(y, 0, 1));
    const e = endOfYear(s);
    const cappedEnd = e > to ? to : e;
    const cappedStart = s < from ? from : s;
    years.push({
      key: `${y}`,
      label: `${y}`,
      start: format(cappedStart, "yyyy-MM-dd"),
      end: format(cappedEnd, "yyyy-MM-dd"),
    });
    y++;
  }
  return years;
}

// ---------- data hook ----------

export function useBalanceSheet(
  dateFrom: string,
  dateTo: string,
  viewPeriod: ViewPeriod,
  displayCurrency: DisplayCurrency,
) {
  const periods = useMemo(
    () => buildPeriods(dateFrom, dateTo, viewPeriod),
    [dateFrom, dateTo, viewPeriod],
  );
  const lastEnd = periods.length ? periods[periods.length - 1].end : dateTo;

  // Exchange rates (all pairs, all history) — enough to convert at any date.
  const ratesQ = useQuery({
    queryKey: ["bs-rates"],
    queryFn: async (): Promise<RateRow[]> => {
      const rows = await paginateSupabase<any>((from, to) =>
        supabase
          .from("exchange_rates" as any)
          .select("currency_pair, rate, effective_date, bank_account_id, rate_type")
          .order("effective_date", { ascending: false })
          .range(from, to),
      );
      return rows as RateRow[];
    },
    staleTime: 5 * 60_000,
  });

  const accountsQ = useQuery({
    queryKey: ["bs-bank-accounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_accounts")
        .select("id, name, currency, exchange_rate_type, is_active");
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        name: string;
        currency: string;
        exchange_rate_type: string | null;
        is_active: boolean;
      }>;
    },
    staleTime: 60_000,
  });

  const accountIds = useMemo(
    () => (accountsQ.data ?? []).filter((a) => a.is_active).map((a) => a.id),
    [accountsQ.data],
  );

  // Daily in/out per bank account for the entire history up to lastEnd (via RPC).
  const dailyTxQ = useQuery({
    queryKey: ["bs-daily-tx", accountIds, lastEnd],
    enabled: accountIds.length > 0 && !!lastEnd,
    queryFn: async () => {
      const to = new Date(`${lastEnd}T00:00:00Z`);
      to.setUTCDate(to.getUTCDate() + 1);
      const { data, error } = await (supabase as any).rpc("get_bank_tx_daily_in_out", {
        p_account_ids: accountIds,
        p_from: `${HISTORY_START}T00:00:00+00:00`,
        p_to: to.toISOString(),
      });
      if (error) throw error;
      return (data ?? []) as Array<{
        bank_account_id: string;
        day: string;
        in_amount: number;
        out_amount: number;
        fee_total: number;
        currency: string | null;
      }>;
    },
    staleTime: 60_000,
  });

  // Expenses ≤ lastEnd — used for AP (unpaid), Fixed Assets (Asset category cumulative)
  // and depreciation.
  const expensesQ = useQuery({
    queryKey: ["bs-expenses", lastEnd],
    enabled: !!lastEnd,
    queryFn: async () => {
      return await paginateSupabase<any>((from, to) =>
        supabase
          .from("expenses")
          .select("id, expense_date, total_amount, currency, payment_status, category_id, bank_account_id")
          .lte("expense_date", lastEnd)
          .range(from, to),
      );
    },
    staleTime: 30_000,
  });

  // Revenue per period (ChipChip GMV + B2B sales, both ETB).
  const chipchipQ = useQuery({
    queryKey: ["bs-chipchip", dateFrom, dateTo],
    enabled: !!dateFrom && !!dateTo,
    queryFn: async () => {
      return await paginateSupabase<{ date: string; total_gmv: number }>((from, to) =>
        (supabase as any)
          .from("chipchip_daily_accepted_totals")
          .select("date, total_gmv")
          .gte("date", dateFrom)
          .lte("date", dateTo)
          .range(from, to),
      );
    },
    staleTime: 30_000,
  });

  const b2bQ = useQuery({
    queryKey: ["bs-b2b", dateFrom, dateTo],
    enabled: !!dateFrom && !!dateTo,
    queryFn: async () => {
      return await paginateSupabase<{ sale_date: string; total_revenue: number }>((from, to) =>
        supabase
          .from("b2b_sales_data")
          .select("sale_date, total_revenue")
          .gte("sale_date", dateFrom)
          .lte("sale_date", dateTo)
          .range(from, to),
      );
    },
    staleTime: 30_000,
  });

  // Reconciled daily closing balances per bank account (source of truth from
  // bank statements). Used to anchor cash so historical opening balances that
  // predate the imported transaction ledger aren't lost.
  const balancesQ = useQuery({
    queryKey: ["bs-daily-balances", accountIds, lastEnd],
    enabled: accountIds.length > 0 && !!lastEnd,
    queryFn: async () => {
      return await paginateSupabase<{
        bank_account_id: string;
        balance_date: string;
        closing_balance: number;
      }>((from, to) =>
        supabase
          .from("bank_account_balances")
          .select("bank_account_id, balance_date, closing_balance")
          .in("bank_account_id", accountIds)
          .lte("balance_date", lastEnd)
          .order("balance_date", { ascending: true })
          .range(from, to),
      );
    },
    staleTime: 60_000,
  });

  // AR proxy: 25% of cumulative B2B sales through period end (temporary
  // placeholder until a real receivables feed is available).
  const arB2bSalesQ = useQuery({
    queryKey: ["bs-b2b-sales-all", lastEnd],
    enabled: !!lastEnd,
    queryFn: async () => {
      return await paginateSupabase<{ sale_date: string; total_revenue: number }>((from, to) =>
        supabase
          .from("b2b_sales_data")
          .select("sale_date, total_revenue")
          .lte("sale_date", lastEnd)
          .range(from, to),
      );
    },
    staleTime: 60_000,
  });

  // Open Orders proxy: cumulative B2B invoiced − cumulative B2B delivered,
  // anchored to the first delivery-report date (deliveries feed only started
  // Feb 26 2026; earlier sales have no matching delivery data).
  const openOrdersDeliveriesQ = useQuery({
    queryKey: ["bs-b2b-deliveries-all", lastEnd],
    enabled: !!lastEnd,
    queryFn: async () => {
      return await paginateSupabase<{ delivery_date: string; net_revenue: number }>((from, to) =>
        supabase
          .from("b2b_delivery_reports")
          .select("delivery_date, net_revenue")
          .lte("delivery_date", lastEnd)
          .range(from, to),
      );
    },
    staleTime: 60_000,
  });

  const isLoading =
    ratesQ.isLoading ||
    accountsQ.isLoading ||
    dailyTxQ.isLoading ||
    expensesQ.isLoading ||
    chipchipQ.isLoading ||
    b2bQ.isLoading ||
    balancesQ.isLoading ||
    arB2bSalesQ.isLoading ||
    openOrdersDeliveriesQ.isLoading;

  // ---------- compute rows ----------

  const rows = useMemo<BSRow[]>(() => {
    const rates = ratesQ.data ?? [];
    const accounts = accountsQ.data ?? [];
    const dailyTx = dailyTxQ.data ?? [];
    const expenses = expensesQ.data ?? [];
    const chipchip = chipchipQ.data ?? [];
    const b2b = b2bQ.data ?? [];
    const balances = balancesQ.data ?? [];
    const arB2bSales = arB2bSalesQ.data ?? [];
    const b2bDeliveries = openOrdersDeliveriesQ.data ?? [];

    // Anchor: first delivery-report date. B2B sales before this date have no
    // matching delivery data, so we exclude them from Open Orders.
    let deliveryAnchor = "9999-12-31";
    for (const d of b2bDeliveries) {
      const dd = String(d.delivery_date).slice(0, 10);
      if (dd < deliveryAnchor) deliveryAnchor = dd;
    }

    const accById = new Map(accounts.map((a) => [a.id, a]));

    // pre-index tx by account → sorted day list
    const txByAccount = new Map<string, Array<{ day: string; net: number }>>();
    for (const row of dailyTx) {
      const list = txByAccount.get(row.bank_account_id) ?? [];
      const net =
        (Number(row.in_amount) || 0) -
        (Number(row.out_amount) || 0) -
        (Number(row.fee_total) || 0);
      list.push({ day: String(row.day).slice(0, 10), net });
      txByAccount.set(row.bank_account_id, list);
    }
    for (const list of txByAccount.values()) list.sort((a, b) => a.day.localeCompare(b.day));

    // Reconciled closing balances per account, sorted by date ascending.
    // These come from imported bank statements and are the source of truth
    // for cash on any date they cover.
    const balancesByAccount = new Map<string, Array<{ day: string; balance: number }>>();
    for (const row of balances) {
      const list = balancesByAccount.get(row.bank_account_id) ?? [];
      list.push({ day: String(row.balance_date).slice(0, 10), balance: Number(row.closing_balance) || 0 });
      balancesByAccount.set(row.bank_account_id, list);
    }
    for (const list of balancesByAccount.values()) list.sort((a, b) => a.day.localeCompare(b.day));

    const convertAt = (
      amount: number,
      from: string,
      periodEnd: string,
      bankAccountId?: string | null,
    ): number => {
      if (!Number.isFinite(amount) || amount === 0) return 0;
      const r = convertAsOfForAccount(amount, from, displayCurrency, periodEnd, rates, bankAccountId);
      return r ? r.converted : 0;
    };

    const cashPerPeriod = new Map<string, number>();
    const apPerPeriod = new Map<string, number>();
    const faCostPerPeriod = new Map<string, number>();
    const accDepPerPeriod = new Map<string, number>();
    const openOrdersPerPeriod = new Map<string, number>();

    // Revenue per period key
    const revPerPeriod = new Map<string, number>();
    const isDateInPeriod = (d: string, p: Period) => d >= p.start && d <= p.end;

    // AR (Accounts Receivable) — outstanding B2B credit orders (unpaid at p.end).
    const arPerPeriod = new Map<string, number>();

    for (const p of periods) {
      // Cash per period: use the latest reconciled closing balance from
      // bank_account_balances on or before p.end for each account. These are
      // imported from bank statements and are the source of truth for cash.
      // No transaction roll-forward — avoids double-counting when a later
      // statement covers those same transactions.
      let cash = 0;
      for (const acc of accounts) {
        if (!acc.is_active) continue;
        const balList = balancesByAccount.get(acc.id) ?? [];
        let bal: number | null = null;
        for (let i = balList.length - 1; i >= 0; i--) {
          if (balList[i].day <= p.end) {
            bal = balList[i].balance;
            break;
          }
        }
        if (bal === null) continue; // no statement yet → contribute nothing
        cash += convertAt(bal, acc.currency, p.end, acc.id);
      }
      cashPerPeriod.set(p.key, cash);

      // AR proxy: 25% of B2B sales within this period (p.start..p.end)
      // — not cumulative — temporary placeholder until real receivables feed.
      let salesEtb = 0;
      for (const s of arB2bSales) {
        const sd = String(s.sale_date);
        if (sd >= p.start && sd <= p.end) salesEtb += Number(s.total_revenue) || 0;
      }
      const arEtb = salesEtb * 0.25;
      arPerPeriod.set(p.key, convertAt(arEtb, "ETB", p.end));





      // AP: unpaid expenses with expense_date ≤ p.end
      // Fixed Assets: cumulative Asset-category expenses ≤ p.end
      let ap = 0;
      let faCost = 0;
      let accDep = 0;
      for (const e of expenses) {
        if (e.expense_date > p.end) continue;
        const amt = Number(e.total_amount) || 0;
        if (amt === 0) continue;
        const converted = convertAt(amt, e.currency || "ETB", p.end, e.bank_account_id);

        if (e.payment_status !== "paid" && e.payment_status !== "cancelled") {
          ap += converted;
        }
        if (e.category_id === ASSET_CATEGORY_ID && e.payment_status === "paid") {
          faCost += converted;
          // straight-line: months elapsed since expense_date, capped at 60
          const months = Math.max(
            0,
            Math.min(
              DEPRECIATION_MONTHS,
              differenceInCalendarDays(parseISO(p.end), parseISO(e.expense_date)) / 30,
            ),
          );
          accDep += (converted * months) / DEPRECIATION_MONTHS;
        }
      }
      apPerPeriod.set(p.key, ap);
      faCostPerPeriod.set(p.key, faCost);
      accDepPerPeriod.set(p.key, accDep);

      // Revenue in period (kept for other rows / totals)
      let revETB = 0;
      for (const r of chipchip) if (isDateInPeriod(r.date, p)) revETB += Number(r.total_gmv) || 0;
      for (const r of b2b) if (isDateInPeriod(r.sale_date, p)) revETB += Number(r.total_revenue) || 0;
      revPerPeriod.set(p.key, convertAt(revETB, "ETB", p.end));

      // Open Orders = cumulative B2B invoiced − cumulative B2B delivered,
      // both anchored to the first delivery-report date. Represents B2B
      // orders sold but not yet delivered as of p.end.
      let soldEtb = 0;
      for (const s of arB2bSales) {
        const sd = String(s.sale_date).slice(0, 10);
        if (sd >= deliveryAnchor && sd <= p.end) soldEtb += Number(s.total_revenue) || 0;
      }
      let deliveredEtb = 0;
      for (const d of b2bDeliveries) {
        const dd = String(d.delivery_date).slice(0, 10);
        if (dd <= p.end) deliveredEtb += Number(d.net_revenue) || 0;
      }
      const openOrdersETB = Math.max(0, soldEtb - deliveredEtb);
      openOrdersPerPeriod.set(p.key, convertAt(openOrdersETB, "ETB", p.end));
    }

    // Assemble rows
    const mk = (
      base: Omit<BSRow, "values">,
      compute: (p: Period) => number,
    ): BSRow => {
      const values = new Map<string, number>();
      for (const p of periods) values.set(p.key, compute(p));
      return { ...base, values };
    };

    const cashRow = mk({ key: "cash", label: "Cash and Cash Equivalents", indent: 1 }, (p) => cashPerPeriod.get(p.key) ?? 0);
    const arRow = mk({ key: "ar", label: "Accounts Receivable", indent: 1 }, (p) => arPerPeriod.get(p.key) ?? 0);
    const invRow = mk({ key: "inv", label: "Inventory", indent: 1 }, () => 0);
    const totalCA = mk(
      { key: "tot-ca", label: "Total Current Assets", isSubtotal: true, indent: 0 },
      (p) => (cashRow.values.get(p.key) ?? 0) + (arRow.values.get(p.key) ?? 0) + (invRow.values.get(p.key) ?? 0),
    );

    const faCostRow = mk({ key: "fa-cost", label: "Total Fixed Assets (Accumulated Cost)", indent: 1 }, (p) => faCostPerPeriod.get(p.key) ?? 0);
    const accDepRow = mk({ key: "acc-dep", label: "Accumulated Depreciation", indent: 1 }, (p) => accDepPerPeriod.get(p.key) ?? 0);
    const nbvRow = mk(
      { key: "nbv", label: "Net Fixed Assets (NBV)", indent: 1 },
      (p) => (faCostRow.values.get(p.key) ?? 0) - (accDepRow.values.get(p.key) ?? 0),
    );
    const otherNCARow = mk({ key: "other-nca", label: "Other Non-Current Assets", indent: 1 }, () => 0);
    const totalNCA = mk(
      { key: "tot-nca", label: "Total Non-Current Assets", isSubtotal: true, indent: 0 },
      (p) => (nbvRow.values.get(p.key) ?? 0) + (otherNCARow.values.get(p.key) ?? 0),
    );

    const totalAssets = mk(
      { key: "tot-assets", label: "Total Assets", isTotal: true, indent: 0 },
      (p) => (totalCA.values.get(p.key) ?? 0) + (totalNCA.values.get(p.key) ?? 0),
    );

    const openOrdersRow = mk({ key: "open-orders", label: "Open Orders", indent: 1 }, (p) => openOrdersPerPeriod.get(p.key) ?? 0);
    const deferredRow = mk({ key: "deferred", label: "Deferred Revenue (Wallet)", indent: 1 }, () => 0);
    const apRow = mk({ key: "ap", label: "Accounts Payable", indent: 1 }, (p) => apPerPeriod.get(p.key) ?? 0);
    const shortLoansRow = mk({ key: "short-loans", label: "Short-term Loans", indent: 1 }, () => 0);
    const accruedRow = mk({ key: "accrued", label: "Accrued Expenses", indent: 1 }, () => 0);
    const totalCL = mk(
      { key: "tot-cl", label: "Total Current Liabilities", isSubtotal: true, indent: 0 },
      (p) =>
        (openOrdersRow.values.get(p.key) ?? 0) +
        (deferredRow.values.get(p.key) ?? 0) +
        (apRow.values.get(p.key) ?? 0) +
        (shortLoansRow.values.get(p.key) ?? 0) +
        (accruedRow.values.get(p.key) ?? 0),
    );

    const longLoansRow = mk({ key: "long-loans", label: "Long-term Loans", indent: 1 }, () => 0);
    const otherNCLRow = mk({ key: "other-ncl", label: "Other Non-Current Liabilities", indent: 1 }, () => 0);
    const totalNCL = mk(
      { key: "tot-ncl", label: "Total Non-Current Liabilities", isSubtotal: true, indent: 0 },
      (p) => (longLoansRow.values.get(p.key) ?? 0) + (otherNCLRow.values.get(p.key) ?? 0),
    );

    const totalLiab = mk(
      { key: "tot-liab", label: "Total Liabilities", isTotal: true, indent: 0 },
      (p) => (totalCL.values.get(p.key) ?? 0) + (totalNCL.values.get(p.key) ?? 0),
    );

    const totalEquity = mk(
      { key: "tot-equity", label: "Total Equity (plug)", isSubtotal: true, indent: 1 },
      (p) => (totalAssets.values.get(p.key) ?? 0) - (totalLiab.values.get(p.key) ?? 0),
    );

    const totalLE = mk(
      { key: "tot-le", label: "Total Liabilities and Equity", isTotal: true, indent: 0 },
      (p) => (totalLiab.values.get(p.key) ?? 0) + (totalEquity.values.get(p.key) ?? 0),
    );

    const checkRow = mk(
      { key: "check", label: "Check: Assets − (Liab + Equity)", isCheck: true, indent: 0 },
      (p) => (totalAssets.values.get(p.key) ?? 0) - (totalLE.values.get(p.key) ?? 0),
    );

    return [
      { key: "h-assets", label: "Assets", isHeader: true, values: new Map() },
      cashRow,
      arRow,
      invRow,
      totalCA,
      { key: "h-nca", label: "Non-Current Assets", isHeader: true, values: new Map() },
      faCostRow,
      accDepRow,
      nbvRow,
      otherNCARow,
      totalNCA,
      totalAssets,
      { key: "h-cl", label: "Current Liabilities", isHeader: true, values: new Map() },
      openOrdersRow,
      deferredRow,
      apRow,
      shortLoansRow,
      accruedRow,
      totalCL,
      { key: "h-ncl", label: "Non-Current Liabilities", isHeader: true, values: new Map() },
      longLoansRow,
      otherNCLRow,
      totalNCL,
      totalLiab,
      { key: "h-eq", label: "Equity", isHeader: true, values: new Map() },
      totalEquity,
      totalLE,
      checkRow,
    ];
  }, [
    periods,
    ratesQ.data,
    accountsQ.data,
    dailyTxQ.data,
    expensesQ.data,
    chipchipQ.data,
    b2bQ.data,
    balancesQ.data,
    arB2bSalesQ.data,
    openOrdersDeliveriesQ.data,
    displayCurrency,
  ]);

  return { rows, periods, isLoading };
}
